
import React from "react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

export interface BrandIconProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: LucideIcon;
  size?: "sm" | "md" | "lg" | "xl";
  color?: "primary" | "secondary" | "muted" | "foreground";
  background?: boolean;
  className?: string;
}

/**
 * Icon component that follows the VirtuStaff brand guidelines
 */
export const BrandIcon: React.FC<BrandIconProps> = ({
  icon: Icon,
  size = "md",
  color = "primary",
  background = false,
  className,
  ...props
}) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
    xl: "w-8 h-8",
  };

  const bgSizeClasses = {
    sm: "p-1.5",
    md: "p-2",
    lg: "p-2.5",
    xl: "p-3",
  };

  const colorClasses = {
    primary: "text-primary",
    secondary: "text-secondary",
    muted: "text-muted-foreground",
    foreground: "text-foreground",
  };

  // Create background color class based on the color prop
  const getBgColorClass = () => {
    switch(color) {
      case "primary":
        return "bg-primary/10";
      case "secondary":
        return "bg-secondary/10";
      case "muted":
        return "bg-muted-foreground/10";
      case "foreground":
        return "bg-foreground/10";
      default:
        return "bg-primary/10";
    }
  };

  return (
    <div
      className={cn(
        "inline-flex items-center justify-center",
        background && getBgColorClass(),
        background && "rounded-full",
        background && bgSizeClasses[size],
        className
      )}
      {...props}
    >
      <Icon className={cn(sizeClasses[size], colorClasses[color])} />
    </div>
  );
};
